/**
 * Phase 3C - what changed between two saved versions of a deal.
 *
 * The problem: "at ask" and "at counter" have been two unlinked PDFs, so nothing could
 * say what actually changed or what the change bought. The delivered corpus is full of
 * these pairs. A diff turns them into one story - "price came down $480,000, IRR went
 * from 4.12% to 6.92%, verdict moved NO GO to CAUTION".
 *
 * ⚠️ `verdictChange` is typed `string`, not the `Verdict` union, and that is deliberate. Versions
 * saved before 2026-08-26 recorded the old three-state vocabulary, so a snapshot can legitimately
 * carry "REVIEW". Widening it to the union would either reject real history or force a rewrite of
 * stored versions - and a diff should report what was actually recorded at the time.
 *
 * Metrics are read from the stored snapshot and NEVER recomputed. A version records what
 * was put in front of a client, not what today's engine would produce from the same
 * inputs - Phase 3A showed those can differ, since modelling the real lease escalations
 * moved Bear Valley from REVIEW to GO without any input changing.
 */

export interface VersionSnapshot {
  label: string;
  snapshot: Record<string, unknown>;
  metrics: Record<string, unknown>;
}

export interface InputChange {
  field: string;
  from: unknown;
  to: unknown;
}

export interface MetricChange {
  metric: string;
  from: number;
  to: number;
  delta: number;
}

export interface VersionDiff {
  from: string;
  to: string;
  inputChanges: InputChange[];
  metricChanges: MetricChange[];
  verdictChange: { from: string; to: string } | null;
  isIdentical: boolean;
}

/**
 * Bookkeeping that would otherwise dominate every diff. `updated_at` in particular
 * changes on every save, so without this a diff of two identical models is never empty.
 */
const IGNORED = new Set([
  'id', 'property_id', 'created_by', 'created_at', 'updated_at',
  'deal_analysis_id', 'brief_parsed_at',
]);

/** Metrics worth reporting, in no particular order - the diff sorts by movement. */
const METRIC_KEYS = ['irr', 'equity_multiple', 'avg_cash_on_cash', 'dscr', 'cap_rate', 'purchase_price'];

/** null, undefined and missing all mean "not set" and must not read as a change. */
const isAbsent = (v: unknown): boolean => v === null || v === undefined;

const sameValue = (a: unknown, b: unknown): boolean => {
  if (isAbsent(a) && isAbsent(b)) return true;
  if (isAbsent(a) !== isAbsent(b)) return false;
  if (typeof a === 'object' || typeof b === 'object') {
    // Arrays and jsonb blobs compare by value. Identity comparison would report every
    // reloaded schedule as a change.
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return a === b;
};

const asNumber = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

export const diffVersions = (a: VersionSnapshot, b: VersionSnapshot): VersionDiff => {
  const fields = new Set([...Object.keys(a.snapshot ?? {}), ...Object.keys(b.snapshot ?? {})]);

  const inputChanges: InputChange[] = [];
  for (const field of fields) {
    if (IGNORED.has(field)) continue;
    const from = a.snapshot?.[field];
    const to = b.snapshot?.[field];
    if (!sameValue(from, to)) inputChanges.push({ field, from, to });
  }
  inputChanges.sort((x, y) => x.field.localeCompare(y.field));

  const metricChanges: MetricChange[] = [];
  for (const metric of METRIC_KEYS) {
    const from = asNumber(a.metrics?.[metric]);
    const to = asNumber(b.metrics?.[metric]);
    if (from === null || to === null) continue;
    if (from === to) continue;
    metricChanges.push({ metric, from, to, delta: to - from });
  }
  // Biggest mover first, so the reason the version was saved reads at the top.
  metricChanges.sort((x, y) => Math.abs(y.delta) - Math.abs(x.delta));

  const vFrom = a.metrics?.verdict;
  const vTo = b.metrics?.verdict;
  const verdictChange =
    typeof vFrom === 'string' && typeof vTo === 'string' && vFrom !== vTo
      ? { from: vFrom, to: vTo }
      : null;

  return {
    from: a.label,
    to: b.label,
    inputChanges,
    metricChanges,
    verdictChange,
    isIdentical: inputChanges.length === 0 && metricChanges.length === 0 && !verdictChange,
  };
};
